import { useState, useEffect, useCallback } from 'react'
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Star,
  Download,
  TrendingUp,
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../services/api'

// ── Types ────────────────────────────────────────────────────────────

interface RatingUser {
  id: string
  name: string
}

interface RatingPickup {
  id: string
  address: string
}

interface Rating {
  id: string
  score: number
  comment: string | null
  createdAt: string
  rater: RatingUser
  ratee: RatingUser
  pickup: RatingPickup | null
}

interface Pagination {
  page: number
  limit: number
  total: number
  totalPages: number
}

interface RatingsResponse {
  ratings: Rating[]
  pagination: Pagination
}

// ── CSV Export Utility ────────────────────────────────────────────────

function exportToCsv(headers: string[], rows: string[][], filename: string) {
  const csv = [
    headers.join(','),
    ...rows.map((r) =>
      r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ),
  ].join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

// ── Star Rating Display ──────────────────────────────────────────────

function StarRating({ score }: { score: number }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${score} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => (
        <Star
          key={i}
          size={14}
          className={
            i < score
              ? 'fill-yellow-400 text-yellow-400'
              : 'fill-none text-gray-300'
          }
        />
      ))}
      <span className="ml-1.5 text-xs font-semibold text-[#1A1A2E]">{score}</span>
    </div>
  )
}

// ── Constants ────────────────────────────────────────────────────────

const PAGE_SIZE = 20

const SCORE_OPTIONS = [
  { value: '', label: 'All Scores' },
  { value: '5-5', label: '5 Stars' },
  { value: '4-5', label: '4+ Stars' },
  { value: '3-5', label: '3+ Stars' },
  { value: '1-2', label: '1-2 Stars' },
  { value: '1-1', label: '1 Star Only' },
]

// ── Component ────────────────────────────────────────────────────────

export default function Ratings() {
  const [ratings, setRatings] = useState<Rating[]>([])
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  })
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [scoreFilter, setScoreFilter] = useState('')
  const [page, setPage] = useState(1)

  // Summary stats computed from response
  const [allRatings, setAllRatings] = useState<Rating[]>([])

  const loadRatings = useCallback(async () => {
    setLoading(true)
    try {
      const [minScore, maxScore] = scoreFilter
        ? scoreFilter.split('-').map(Number)
        : [undefined, undefined]

      const res = await api.get<{ data: RatingsResponse }>('/admin/ratings', {
        params: {
          page,
          limit: PAGE_SIZE,
          ...(search ? { search } : {}),
          ...(minScore !== undefined ? { minScore } : {}),
          ...(maxScore !== undefined ? { maxScore } : {}),
        },
      })

      const data = res.data?.data ?? res.data
      setRatings((data as RatingsResponse)?.ratings ?? [])
      setPagination(
        (data as RatingsResponse)?.pagination ?? {
          page: 1,
          limit: PAGE_SIZE,
          total: 0,
          totalPages: 0,
        }
      )

      // On first load (no filters), cache all ratings for summary stats
      if (!scoreFilter && !search && page === 1) {
        setAllRatings((data as RatingsResponse)?.ratings ?? [])
      }
    } catch {
      toast.error('Failed to load ratings')
    } finally {
      setLoading(false)
    }
  }, [page, scoreFilter, search])

  useEffect(() => {
    loadRatings()
  }, [loadRatings])

  // ── Summary Calculations ──────────────────────────────────────────

  const summarySource = allRatings.length > 0 ? allRatings : ratings
  const totalReviews = pagination.total
  const averageRating =
    summarySource.length > 0
      ? (
          summarySource.reduce((sum, r) => sum + r.score, 0) /
          summarySource.length
        ).toFixed(1)
      : '0.0'
  const fiveStarCount = summarySource.filter((r) => r.score === 5).length
  const oneStarCount = summarySource.filter((r) => r.score === 1).length

  // ── Client-side search filter on loaded data ──────────────────────

  const filtered = ratings.filter((r) => {
    if (!search) return true
    const q = search.toLowerCase()
    return (
      r.rater?.name?.toLowerCase().includes(q) ||
      r.ratee?.name?.toLowerCase().includes(q) ||
      r.pickup?.address?.toLowerCase().includes(q) ||
      r.comment?.toLowerCase().includes(q)
    )
  })

  // ── CSV Export Handler ────────────────────────────────────────────

  const handleExportCsv = () => {
    if (filtered.length === 0) {
      toast.error('No ratings to export')
      return
    }

    const headers = ['Date', 'Resident', 'Collector', 'Rating', 'Comment', 'Pickup Address']
    const rows = filtered.map((r) => [
      new Date(r.createdAt).toLocaleDateString(),
      r.rater?.name ?? '—',
      r.ratee?.name ?? '—',
      String(r.score),
      r.comment ?? '',
      r.pickup?.address ?? '—',
    ])

    exportToCsv(headers, rows, `ratings-export-${new Date().toISOString().slice(0, 10)}.csv`)
    toast.success('Ratings exported successfully')
  }

  // ── Pagination ────────────────────────────────────────────────────

  const totalPages = pagination.totalPages || Math.ceil(pagination.total / PAGE_SIZE)

  // Generate visible page numbers with ellipsis support
  const getVisiblePages = (): (number | 'ellipsis')[] => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1)
    const pages: (number | 'ellipsis')[] = []
    if (page <= 3) {
      for (let i = 1; i <= Math.min(5, totalPages); i++) pages.push(i)
    } else if (page >= totalPages - 2) {
      for (let i = Math.max(1, totalPages - 4); i <= totalPages; i++) pages.push(i)
    } else {
      pages.push(page - 2, page - 1, page, page + 1, page + 2)
    }
    return pages
  }

  // ── Render ────────────────────────────────────────────────────────

  return (
    <div className="space-y-5">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          {
            label: 'Average Rating',
            value: averageRating,
            icon: TrendingUp,
            color: 'text-green-700',
            iconBg: 'bg-green-50 text-green-600',
          },
          {
            label: 'Total Reviews',
            value: totalReviews,
            icon: MessageSquare,
            color: 'text-blue-600',
            iconBg: 'bg-blue-50 text-blue-600',
          },
          {
            label: '5-Star Reviews',
            value: fiveStarCount,
            icon: ThumbsUp,
            color: 'text-yellow-600',
            iconBg: 'bg-yellow-100 text-yellow-700',
          },
          {
            label: '1-Star Reviews',
            value: oneStarCount,
            icon: ThumbsDown,
            color: 'text-red-600',
            iconBg: 'bg-red-50 text-red-600',
          },
        ].map(({ label, value, icon: Icon, color, iconBg }) => (
          <div
            key={label}
            className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <p className="text-xs text-[#6B7280] font-medium">{label}</p>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconBg}`}>
                <Icon size={16} />
              </div>
            </div>
            <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="flex-1 flex items-center gap-2 bg-[#F8FAF9] border border-gray-200 rounded-xl px-3 py-2">
            <Search className="w-4 h-4 text-[#6B7280]" />
            <input
              type="text"
              placeholder="Search by resident, collector, or address..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              className="flex-1 bg-transparent text-sm text-[#1A1A2E] placeholder-[#6B7280] outline-none"
            />
          </div>

          {/* Score Range */}
          <select
            value={scoreFilter}
            onChange={(e) => {
              setScoreFilter(e.target.value)
              setPage(1)
            }}
            className="bg-[#F8FAF9] border border-gray-200 rounded-xl px-3 py-2 text-sm text-[#1A1A2E] outline-none cursor-pointer min-w-[140px]"
            aria-label="Filter by score range"
          >
            {SCORE_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>

          {/* Export CSV */}
          <button
            onClick={handleExportCsv}
            className="flex items-center gap-1.5 text-xs font-semibold text-white bg-primary-600 px-4 py-2 rounded-xl hover:bg-primary-700 transition-colors whitespace-nowrap"
          >
            <Download size={13} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <p className="text-sm font-semibold text-[#1A1A2E]">
            {pagination.total} rating{pagination.total !== 1 ? 's' : ''}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#F8FAF9]">
                  {['Date', 'Resident', 'Collector', 'Rating', 'Comment', 'Pickup Address'].map(
                    (h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] whitespace-nowrap"
                      >
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((rating) => (
                  <tr
                    key={rating.id}
                    className="hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-4 py-3 text-xs text-[#6B7280] whitespace-nowrap">
                      {new Date(rating.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-xs font-medium text-[#1A1A2E] whitespace-nowrap">
                      {rating.rater?.name ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#6B7280] whitespace-nowrap">
                      {rating.ratee?.name ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      <StarRating score={rating.score} />
                    </td>
                    <td className="px-4 py-3 text-xs text-[#6B7280] max-w-[240px]">
                      {rating.comment ? (
                        <span className="line-clamp-2">{rating.comment}</span>
                      ) : (
                        <span className="italic text-gray-400">No comment</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#6B7280] max-w-[180px] truncate">
                      {rating.pickup?.address ?? '—'}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-8 text-center text-sm text-[#6B7280]"
                    >
                      No ratings found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
            <p className="text-xs text-[#6B7280]">
              Showing{' '}
              {Math.min((page - 1) * PAGE_SIZE + 1, pagination.total)}
              {' '}–{' '}
              {Math.min(page * PAGE_SIZE, pagination.total)} of{' '}
              {pagination.total}
            </p>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg text-[#6B7280] hover:bg-gray-100 disabled:opacity-40 transition-colors"
                aria-label="Previous page"
              >
                <ChevronLeft size={16} />
              </button>
              {getVisiblePages().map((p, idx) =>
                p === 'ellipsis' ? (
                  <span
                    key={`ellipsis-${idx}`}
                    className="w-7 h-7 flex items-center justify-center text-xs text-[#6B7280]"
                  >
                    ...
                  </span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-7 h-7 rounded-lg text-xs font-semibold transition-colors ${
                      p === page
                        ? 'bg-primary-600 text-white'
                        : 'text-[#6B7280] hover:bg-gray-100'
                    }`}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages || totalPages === 0}
                className="p-1.5 rounded-lg text-[#6B7280] hover:bg-gray-100 disabled:opacity-40 transition-colors"
                aria-label="Next page"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
